import React from "react";
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

const UpdateUsageLimitComponent = ({
  handleUsageLimitChange,
  handleUsageLimitDataChange,
  handleUsageLimitValueChange,
  handleClearUsageLimitValue,
  usageLimitData,
  usageLimitRequired,
  loading,
}) => {
  // API expects: HOURS, DAYS, MONTH (plural)
  // But response gives: HOUR, DAY, MONTH (singular)
  // So we need to map between them
  const frequencyOptions = [
    { value: "HOURS", label: "Hour", apiValue: "HOURS" },
    { value: "DAYS", label: "Day", apiValue: "DAYS" },
    { value: "MONTH", label: "Month", apiValue: "MONTH" },
  ];

  const limitOnOptions = [
    { value: "AMOUNT", label: "Amount" },
    { value: "TRANSACTION", label: "Transaction" },
  ];

  // Helper function to convert API frequency to UI frequency
  const convertApiFrequencyToUI = (apiFrequency) => {
    if (apiFrequency === "HOUR") return "HOURS";
    if (apiFrequency === "DAY") return "DAYS";
    if (apiFrequency === "MONTH") return "MONTH";
    return apiFrequency;
  };

  // Helper function to convert UI frequency to API frequency
  const convertUIFrequencyToAPI = (uiFrequency) => {
    if (uiFrequency === "HOURS") return "HOUR";
    if (uiFrequency === "DAYS") return "DAY";
    if (uiFrequency === "MONTH") return "MONTH";
    return uiFrequency;
  };

  const preventScroll = (e) => {
    e.preventDefault();
    e.target.blur();
  };

  const preventArrowKeys = (e) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
    }
  };

  const handleValueChange = (e) => {
    const value = e.target.value;
    
    if (value === "") {
      handleUsageLimitValueChange(e);
      return;
    }
    
    if (usageLimitData.limitOn === "AMOUNT") {
      if (/^\d*\.?\d{0,2}$/.test(value) && value.length <= 20) {
        handleUsageLimitValueChange(e);
      }
    } else if (usageLimitData.limitOn === "TRANSACTION") {
      if (/^\d*$/.test(value) && value.length <= 20) {
        handleUsageLimitValueChange(e);
      }
    } else {
      if (/^\d*\.?\d*$/.test(value) && value.length <= 20) {
        handleUsageLimitValueChange(e);
      }
    }
  };

  const getHelperText = () => {
    if (!usageLimitData.limitOn && !usageLimitData.frequency) {
      return "Please select limit type and frequency first";
    }
    if (!usageLimitData.limitOn) {
      return "Please select what you want to limit";
    }
    if (!usageLimitData.frequency) {
      return "Please select frequency";
    }
    
    if (usageLimitData.limitOn === "AMOUNT") {
      if (usageLimitData.frequency === "HOURS") {
        return "Maximum amount (in ₹) allowed per hour";
      } else if (usageLimitData.frequency === "DAYS") {
        return "Maximum amount (in ₹) allowed per day";
      } else if (usageLimitData.frequency === "MONTH") {
        return "Maximum amount (in ₹) allowed per month";
      }
    } else if (usageLimitData.limitOn === "TRANSACTION") {
      if (usageLimitData.frequency === "HOURS") {
        return "Maximum number of transactions allowed per hour";
      } else if (usageLimitData.frequency === "DAYS") {
        return "Maximum number of transactions allowed per day";
      } else if (usageLimitData.frequency === "MONTH") {
        return "Maximum number of transactions allowed per month";
      }
    }
    
    return "Enter the limit value";
  };

  const getPlaceholder = () => {
    if (usageLimitData.limitOn === "AMOUNT") {
      return "Enter amount limit (e.g., 1000.00)";
    } else if (usageLimitData.limitOn === "TRANSACTION") {
      return "Enter transaction count (e.g., 100)";
    }
    return "Enter limit value";
  };

  // Handle radio change - converts "Yes"/"No" to proper values
  const handleRadioChange = (e) => {
    const value = e.target.value;
    handleUsageLimitChange(value);
  };

  // Handle limit on change - store as is for UI
  const handleLimitOnChange = (value) => {
    handleUsageLimitDataChange("limitOn", value);
  };

  // Handle frequency change - store UI value (HOURS/DAYS/MONTH)
  const handleFrequencyChange = (value) => {
    handleUsageLimitDataChange("frequency", value);
  };

  const isLoading = loading;

  return (
    <Box sx={{ maxWidth: 800, margin: "auto", pb: 4 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: "#333", mb: 1 }}>
        Usage Limit
      </Typography>
      <p style={{ color: "#666" }}>
        Define spending thresholds and transaction volume controls for this corporate account.
      </p>
      
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
          <CircularProgress size={32} />
          <Typography sx={{ ml: 2, color: '#666' }}>Loading usage limit data...</Typography>
        </Box>
      )}
      
      {!isLoading && (
        <>
          <Box sx={{ background: "#f8f9fa", borderRadius: 2, p: 3, mb: 1 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: "#333", mb: 1 }}>
              Do you want to set a usage limit?
            </Typography>

            <RadioGroup
              value={usageLimitRequired}
              onChange={handleRadioChange}
              row
              sx={{ gap: 3 }}
            >
              <FormControlLabel
                value="Yes"
                control={<Radio sx={{ color: '#14AE78', '&.Mui-checked': { color: '#14AE78' } }} />}
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>Yes</Typography>
                    <Typography variant="caption" sx={{ color: "#666", display: "block" }}>
                      Set a usage limit for corporate
                    </Typography>
                  </Box>
                }
                sx={{
                  background: usageLimitRequired === "Yes" ? "#e8f5e9" : "transparent",
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                  m: 0
                }}
              />
              <FormControlLabel
                value="No"
                control={<Radio sx={{ color: '#14AE78', '&.Mui-checked': { color: '#14AE78' } }} />}
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>No</Typography>
                    <Typography variant="caption" sx={{ color: "#666", display: "block" }}>
                      No usage limit required
                    </Typography>
                  </Box>
                }
                sx={{
                  background: usageLimitRequired === "No" ? "#e8f5e9" : "transparent",
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                  m: 0
                }}
              />
            </RadioGroup>
          </Box>

          {usageLimitRequired === "Yes" && (
            <Box sx={{ background: "white", borderRadius: 2, p: 3, border: "1px solid #e0e0e0" }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#333", mb: 3 }}>
                Configure Usage Limit
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {/* What to limit */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: "#444", mb: 2 }}>
                    What do you want to limit?
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {limitOnOptions.map((option) => (
                      <Button
                        key={option.value}
                        variant={usageLimitData.limitOn === option.value ? "contained" : "outlined"}
                        onClick={() => handleLimitOnChange(option.value)}
                        sx={{
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 500,
                          px: 3,
                          py: 1,
                          ...(usageLimitData.limitOn === option.value ? {
                            background: "#14AE78",
                            color: "white",
                            borderColor: "#14AE78",
                            "&:hover": {
                              background: "#0f8c65",
                              borderColor: "#0f8c65",
                            }
                          } : {
                            borderColor: "#e0e0e0",
                            color: "#666",
                            "&:hover": {
                              borderColor: "#14AE78",
                              color: "#14AE78",
                              background: "rgba(20, 174, 120, 0.04)"
                            }
                          })
                        }}
                      >
                        {option.label}
                      </Button>
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ color: "#666", mt: 1, display: "block" }}>
                    {usageLimitData.limitOn === "AMOUNT" && "Limit based on total amount spent"}
                    {usageLimitData.limitOn === "TRANSACTION" && "Limit based on number of transactions"}
                    {!usageLimitData.limitOn && "Select what you want to limit"}
                  </Typography>
                </Box>

                {/* Frequency */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: "#444", mb: 2 }}>
                    Frequency
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {frequencyOptions.map((option) => (
                      <Button
                        key={option.value}
                        variant={usageLimitData.frequency === option.value ? "contained" : "outlined"}
                        onClick={() => handleFrequencyChange(option.value)}
                        sx={{
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 500,
                          px: 3,
                          py: 1,
                          ...(usageLimitData.frequency === option.value ? {
                            background: "#14AE78",
                            color: "white",
                            borderColor: "#14AE78",
                            "&:hover": {
                              background: "#0f8c65",
                              borderColor: "#0f8c65",
                            }
                          } : {
                            borderColor: "#e0e0e0",
                            color: "#666",
                            "&:hover": {
                              borderColor: "#14AE78",
                              color: "#14AE78",
                              background: "rgba(20, 174, 120, 0.04)"
                            }
                          })
                        }}
                      >
                        {option.label}
                      </Button>
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ color: "#666", mt: 1, display: "block" }}>
                    {usageLimitData.frequency === "HOURS" && "Set hourly limit for corporate usage"}
                    {usageLimitData.frequency === "DAYS" && "Set daily limit for corporate usage"}
                    {usageLimitData.frequency === "MONTH" && "Set monthly limit for corporate usage"}
                    {!usageLimitData.frequency && "Select how often the limit resets"}
                  </Typography>
                </Box>

                {/* Limit Value */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: "#444", mb: 1 }}>
                    Limit Value
                    <span style={{ marginLeft: '8px', fontSize: '12px', color: '#999', fontWeight: 'normal' }}>
                      {usageLimitData.value?.length || 0}/20
                    </span>
                  </Typography>
                  <TextField
                    fullWidth
                    value={usageLimitData.value || ""}
                    onChange={handleValueChange}
                    onWheel={preventScroll}
                    onKeyDown={preventArrowKeys}
                    placeholder={getPlaceholder()}
                    disabled={!usageLimitData.limitOn || !usageLimitData.frequency}
                    type="text"
                    inputProps={{
                      inputMode: usageLimitData.limitOn === "TRANSACTION" ? "numeric" : "decimal",
                      style: {
                        MozAppearance: 'textfield',
                        WebkitAppearance: 'none',
                      },
                      maxLength: 20
                    }}
                    InputProps={{
                      startAdornment: usageLimitData.limitOn === "AMOUNT" ? (
                        <InputAdornment position="start">
                          <span style={{ color: '#666', fontWeight: 500 }}>₹</span>
                        </InputAdornment>
                      ) : null,
                      endAdornment: usageLimitData.value ? (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClearUsageLimitValue}
                            edge="end"
                            size="small"
                            sx={{
                              color: '#999',
                              '&:hover': {
                                color: '#666',
                                backgroundColor: 'rgba(0,0,0,0.04)'
                              }
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: '#fff',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#14AE78',
                          }
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#14AE78',
                            borderWidth: 2,
                          }
                        }
                      },
                      "& input": {
                        "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        }
                      }
                    }}
                  />
                  <Typography variant="caption" sx={{ color: "#666", mt: 1, display: "block" }}>
                    {getHelperText()}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default UpdateUsageLimitComponent;